import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { documentKeys } from '@/features/documents/queries';
import { closeMonth, getRun, isActive, listPeriodRuns, listPeriods, processMonth } from './api';

export const periodKeys = {
  all: ['periods'] as const,
  list: ['periods', 'list'] as const,
  runs: (periodId: string) => ['periods', periodId, 'runs'] as const,
  // outside ['periods'], so refreshing the months when a run finishes doesn't cancel the run's own fetch
  run: (id: string) => ['pipeline-run', id] as const,
};

const POLL_MS = 3000;

export function usePeriods() {
  return useQuery({ queryKey: periodKeys.list, queryFn: ({ signal }) => listPeriods(signal) });
}

export function usePeriodRuns(periodId: string | undefined) {
  return useQuery({
    queryKey: periodKeys.runs(periodId ?? ''),
    queryFn: ({ signal }) => listPeriodRuns(periodId!, signal),
    enabled: !!periodId,
    // keep the history current while a run is in flight
    refetchInterval: (q) => (q.state.data?.some(isActive) ? POLL_MS : false),
  });
}

/** Follows one run until it finishes, then refreshes the months and the library. */
export function useRun(id: string | undefined) {
  const qc = useQueryClient();
  const run = useQuery({
    queryKey: periodKeys.run(id ?? ''),
    queryFn: ({ signal }) => getRun(id!, signal),
    enabled: !!id,
    refetchInterval: (q) => (!q.state.data || isActive(q.state.data) ? POLL_MS : false),
  });
  const finished = !!run.data && !isActive(run.data);
  useEffect(() => {
    if (!finished) return;
    void qc.invalidateQueries({ queryKey: periodKeys.all });
    void qc.invalidateQueries({ queryKey: documentKeys.all });
  }, [finished, id, qc]);
  return run;
}

function useStart(fn: typeof processMonth) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => qc.invalidateQueries({ queryKey: periodKeys.all }),
  });
}

export const useProcessMonth = () => useStart(processMonth);
export const useCloseMonth = () => useStart(closeMonth);
